<!-- Mobile Bottom Navigation Bar (< lg) -->
<nav
  class="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-200/80 dark:border-slate-800 shadow-[0_-4px_25px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_25px_rgba(0,0,0,0.4)] pb-[env(safe-area-inset-bottom,0px)]"
  aria-label="Navigation principale mobile"
>
  <div class="flex h-16 items-stretch px-1">
    @for (tab of navTabs(); track tab.route) {
      <a
        [routerLink]="tab.route"
        routerLinkActive="text-orange-600 font-bold"
        [routerLinkActiveOptions]="{ exact: tab.exact ?? false }"
        #rla="routerLinkActive"
        class="flex-1 min-w-0 flex flex-col items-center justify-center h-full py-1 text-slate-400 dark:text-slate-400 font-medium transition-all active:scale-90 group relative cursor-pointer"
      >
        <div
          class="w-9 h-7 flex items-center justify-center rounded-xl transition-all"
          [class.bg-orange-500/10]="rla.isActive"
          [class.text-orange-600]="rla.isActive"
        >
          <ng-icon [name]="tab.icon" size="20"></ng-icon>
        </div>
        <span
          class="text-[10px] tracking-tight mt-0.5 leading-none transition-all"
          [class.text-orange-600]="rla.isActive"
          [class.font-bold]="rla.isActive"
        >
          {{ tab.label }}
        </span>
        @if (rla.isActive) {
          <span class="w-1 h-1 rounded-full bg-orange-600 mt-1"></span>
        }
      </a>
    }

    <!-- "Plus / Menu" Action Tab -->
    <button
      type="button"
      (click)="toggleMenu()"
      class="flex-1 min-w-0 flex flex-col items-center justify-center h-full py-1 text-slate-400 dark:text-slate-400 font-medium transition-all active:scale-90 group relative cursor-pointer"
      [class.text-orange-600]="isMenuOpen()"
      title="Ouvrir le menu complet"
    >
      <div
        class="w-9 h-7 flex items-center justify-center rounded-xl transition-all"
        [class.bg-orange-500/10]="isMenuOpen()"
        [class.text-orange-600]="isMenuOpen()"
      >
        <ng-icon [name]="isMenuOpen() ? 'x' : 'menu'" size="20"></ng-icon>
      </div>
      <span
        class="text-[10px] tracking-tight mt-0.5 leading-none"
        [class.text-orange-600]="isMenuOpen()"
        [class.font-bold]="isMenuOpen()"
      >
        {{ isMenuOpen() ? 'Fermer' : 'Menu' }}
      </span>
      @if (isMenuOpen()) {
        <span class="w-1 h-1 rounded-full bg-orange-600 mt-1"></span>
      }
    </button>
  </div>
</nav>

<!-- Mobile Menu Bottom Sheet -->
@if (isMenuOpen()) {
  <!-- Backdrop -->
  <div
    class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 animate-fade-in lg:hidden"
    (click)="closeMenu()"
  ></div>

  <!-- Sheet Container -->
  <div
    class="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-[32px] shadow-2xl border-t border-slate-200/80 dark:border-slate-800 flex flex-col max-h-[88vh] animate-slide-up-sheet lg:hidden pb-[calc(env(safe-area-inset-bottom)+1rem)]"
    role="dialog"
    aria-modal="true"
    aria-label="Menu complet"
  >
    <!-- Top Drag Handle -->
    <div class="flex items-center justify-center pt-3 pb-1 cursor-pointer" (click)="closeMenu()">
      <div class="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 active:bg-slate-400 transition-colors"></div>
    </div>

    <!-- User & Club Header Card -->
    <div class="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
      <div class="flex items-center gap-3 min-w-0">
        @if (profile()?.user?.image && profile()?.user?.image !== 'string') {
          <img
            [src]="profile()?.user?.image"
            alt="Avatar"
            class="w-11 h-11 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 shrink-0"
          />
        } @else {
          <div
            class="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-600 to-orange-400 flex items-center justify-center font-head font-bold text-sm text-white shadow-sm shrink-0"
          >
            {{ (profile()?.user?.email?.substring(0, 2) || 'US').toUpperCase() }}
          </div>
        }

        <div class="flex flex-col min-w-0">
          <h4 class="font-bold text-sm text-slate-900 dark:text-white truncate">
            {{ profile()?.user?.first_name ? (profile()?.user?.first_name + ' ' + (profile()?.user?.last_name || '')) : (profile()?.user?.email || 'Utilisateur') }}
          </h4>
          <div class="flex items-center gap-2 mt-0.5">
            <span class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 border border-orange-200/60 dark:border-orange-900/50 uppercase">
              {{ profile()?.user?.role?.name || (localStorageService.getRole() === 'SUPER_ADMIN' ? 'Super Admin' : (localStorageService.getRole() || 'Admin')) }}
            </span>
            @if (activeClub()) {
              <span class="text-[11px] text-slate-500 dark:text-slate-400 font-medium truncate max-w-[120px]">
                {{ activeClub()?.name }}
              </span>
            }
          </div>
        </div>
      </div>

      <button
        type="button"
        (click)="closeMenu()"
        class="w-9 h-9 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-all active:scale-95 cursor-pointer shrink-0"
        title="Fermer"
      >
        <ng-icon name="x" size="18"></ng-icon>
      </button>
    </div>

    <!-- Scrollable Menu Content -->
    <div class="flex-1 overflow-y-auto px-4 py-3 space-y-4 overscroll-contain">
      <!-- Section: Navigation List -->
      <div class="space-y-2">
        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2">
          Fonctionnalités & Gestion
        </p>

        <div class="grid grid-cols-1 gap-2">
          @for (item of sheetMenuItems(); track item.route) {
            <a
              [routerLink]="item.route"
              (click)="closeMenu()"
              class="flex items-center gap-3.5 p-3 rounded-2xl bg-white dark:bg-slate-800/80 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-100/90 dark:border-slate-700/60 active:scale-[0.98] transition-all group cursor-pointer"
            >
              <div
                class="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-200/60 dark:border-orange-900/50 text-orange-600 dark:text-orange-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
              >
                <ng-icon [name]="item.icon" size="20"></ng-icon>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-slate-800 dark:text-white leading-snug">{{ item.label }}</p>
                @if (item.description) {
                  <p class="text-[11px] text-slate-400 truncate">{{ item.description }}</p>
                }
              </div>
              <ng-icon name="chevron-right" size="18" class="text-slate-300 dark:text-slate-600 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors"></ng-icon>
            </a>
          }
        </div>
      </div>

      <!-- Section: Club Switcher (If multiple clubs) -->
      @if (clubs().length > 1) {
        <div class="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2">
            Changer de Club
          </p>
          <div class="grid grid-cols-1 gap-2">
            @for (c of clubs(); track c.id) {
              <button
                type="button"
                (click)="selectClub(c)"
                class="w-full flex items-center justify-between p-3 rounded-2xl border text-left transition-all active:scale-[0.98] cursor-pointer"
                [class.bg-orange-50]="activeClub()?.id === c.id"
                [class.dark:bg-orange-950/40]="activeClub()?.id === c.id"
                [class.border-orange-300]="activeClub()?.id === c.id"
                [class.dark:border-orange-800/60]="activeClub()?.id === c.id"
                [class.border-slate-100]="activeClub()?.id !== c.id"
                [class.dark:border-slate-800]="activeClub()?.id !== c.id"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs"
                    [class.bg-orange-600]="activeClub()?.id === c.id"
                    [class.text-white]="activeClub()?.id === c.id"
                    [class.bg-slate-100]="activeClub()?.id !== c.id"
                    [class.dark:bg-slate-800]="activeClub()?.id !== c.id"
                    [class.text-slate-600]="activeClub()?.id !== c.id"
                    [class.dark:text-slate-300]="activeClub()?.id !== c.id"
                  >
                    {{ c.name?.charAt(0)?.toUpperCase() }}
                  </div>
                  <span class="text-sm font-semibold text-slate-800 dark:text-slate-200">{{ c.name }}</span>
                </div>
                @if (activeClub()?.id === c.id) {
                  <span class="text-xs font-bold text-orange-600 dark:text-orange-400 flex items-center gap-1">
                    Actif
                  </span>
                }
              </button>
            }
          </div>
        </div>
      }

      <!-- Section: PWA Install Shortcut -->
      @if (!pwaService.isStandalone() && (pwaService.canInstall() || pwaService.isIOS())) {
        <div class="pt-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            (click)="triggerInstall()"
            class="w-full flex items-center gap-3.5 p-3 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm shadow-md shadow-orange-500/20 active:scale-[0.98] transition-all cursor-pointer"
          >
            <div class="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <ng-icon name="plus" size="20"></ng-icon>
            </div>
            <div class="flex-1 text-left min-w-0">
              <p class="text-sm font-extrabold leading-tight">Installer l'application GYMO</p>
              <p class="text-[11px] text-white/80 font-normal">Accès rapide sur votre écran d'accueil</p>
            </div>
          </button>
        </div>
      }

      <!-- Section: Logout Action -->
      <div class="pt-2 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          (click)="openLogout()"
          class="w-full flex items-center justify-center gap-2.5 p-3.5 rounded-2xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-sm border border-rose-100 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-400 dark:hover:bg-rose-900/40 transition-all active:scale-[0.98] cursor-pointer"
        >
          <ng-icon name="log-out" size="18"></ng-icon>
          <span>Se déconnecter</span>
        </button>
      </div>
    </div>
  </div>
}

<!-- Logout Confirmation Modal (Bottom Sheet on mobile) -->
<app-modal [isOpen]="showLogoutModal()" title="Déconnexion" (close)="closeLogout()">
  <div class="flex flex-col gap-4">
    <p class="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Confirmation de déconnexion</p>
    <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
      Êtes-vous certain de vouloir vous déconnecter de votre session GYMO ?
    </p>
    <div class="flex justify-end gap-3 mt-4">
      <app-button-form
        label="Annuler"
        variant="outline"
        type="button"
        size="md"
        (onClick)="closeLogout()"
      ></app-button-form>
      <app-button-form
        label="Oui, se déconnecter"
        type="button"
        size="md"
        (onClick)="onLogout()"
      ></app-button-form>
    </div>
  </div>
</app-modal>
